import { useState, useCallback } from 'react';
import { Button } from '@mui/joy';
import Actions from '../../../store/Actions';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';
import { UploadError } from '../../../components/Modal/ErrorDetailsModal/types';

export const useIllioUpload = (
  processedFileId: string | null,
  onShowErrors?: (errors: UploadError[]) => void
) => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadToIllio = useCallback(
    async (rowIndices: number[]) => {
      if (rowIndices.length === 0) {
        showSnackbar({
          title: 'No Rows Selected',
          type: SnackbarType.warning,
          message: 'Please select at least one row to upload',
        });
        return;
      }

      if (!processedFileId) {
        showSnackbar({
          title: 'Upload Failed',
          type: SnackbarType.error,
          message: 'No processed file available for upload',
        });
        return;
      }

      setIsUploading(true);

      try {
        const result = await Actions.uploadProcessedRows(
          processedFileId,
          rowIndices
        );

        console.log(
          `Upload complete: ${result.success_count} succeeded, ${result.fail_count} failed`
        );

        const hasErrors = result.errors && result.errors.length > 0;

        const warningCount = result.warning_count ?? 0;

        const snackbarTitle =
          result.fail_count === 0 && warningCount === 0
            ? 'Upload Successful'
            : warningCount > 0 && result.fail_count === 0
              ? 'Upload Completed with Warnings'
              : 'Upload Completed';

        const snackbarType =
          result.fail_count === 0 && warningCount === 0
            ? SnackbarType.success
            : SnackbarType.warning;

        const snackbarMessage =
          result.fail_count === 0 && warningCount === 0
            ? `${result.success_count}/${rowIndices.length} uploaded successfully`
            : warningCount > 0 && result.fail_count === 0
              ? `${result.success_count + warningCount}/${rowIndices.length} uploaded (${warningCount} with warnings)`
              : `${result.success_count + warningCount}/${rowIndices.length} uploaded successfully`;

        showSnackbar({
          title: snackbarTitle,
          type: snackbarType,
          message: snackbarMessage,
          timeout: hasErrors && onShowErrors ? 3000 : undefined,
          action:
            hasErrors && onShowErrors ? (
              <Button
                size="sm"
                variant="plain"
                onClick={() => onShowErrors(result.errors!)}
              >
                View Details
              </Button>
            ) : undefined,
        });

        return result;
      } catch (error) {
        console.error('Upload failed:', error);
        showSnackbar({
          title: 'Upload Failed',
          type: SnackbarType.error,
          message:
            error instanceof Error
              ? error.message
              : 'Unknown error occurred during upload',
        });
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [processedFileId, onShowErrors]
  );

  return { isUploading, uploadToIllio };
};
